// Story 8.2 (ADO-2011), Slice 1 — the /cart route (GD-2). Renders the shell; the shell's
// header wiring (useHeaderChrome) mounts the active-basket lifecycle, as on every
// full-header screen. Story 8.9 extends it through `CartPageShell`'s `headerActions`.
//
// Story 8.11 (ADO-2020) replaces 8.2's flat default list with the grouped Order Review
// (Figma 4976:1638): a depot card holding one section per catalogue department (heading
// blank for lines with no department, and `N cases`), a table header with LAST ORDERED,
// every line rendered through 8.2's shared `CartLineRow`, and Total cases in the title and
// the footer. The groups, their order and every count come from the server
// (GET /api/v1/basket/review, SL1.0 rules); the page only lays them out. It fetches on
// mount and on a depot switch, and again whenever 8.2's store settles a quantity change or
// removal, so the totals always come from the latest review response.
//
// Below 768px (8.2's `useIsNarrowViewport`) every line renders with 8.2's stacked Option A
// row (`variant="pageMobile"`, Figma 4379:12439, gate decision 6) carrying the 8.11 fields,
// each group opens with the mobile department band (5324:12633: the name and its share of the
// order value over `N cases · €X`) and the page ends with the Total cases row (4393:12515);
// the desktop column header is dropped because the stacked card labels its own LAST ORDERED
// cell. A removal hands focus to the same control on the neighbouring row across every group
// (8.2's `focusNeighbourRow`), else the page title. The review never seeds 8.2's store. The
// lines 8.2's hydrated store holds carry the stepper and trash, and they show, and step from,
// the store's live quantity (that same line's server-reconciled value, ahead of the review
// between a settled edit and the refetch); a line the store has just removed leaves the list
// at once. Story 8.3 (ADO-2012), DV-2 replaces 8.11's rule that a colleague's lines are
// read-only (user decision, ADO #2020, 2026-09-25): colleagues share the cart's one section,
// the store the screen re-reads on opening (`useActiveBasket`) holds every line of it, and so
// a colleague's lines carry the same controls. A row the store does not hold (a line added
// since that read) is read-only with the review's quantity until the next read. When a SKU
// sits in two sections only the first row of a held SKU is editable. The review is read only
// once the store has hydrated (or its hydrate failed), so a direct load never shows the
// cart's lines read-only nor reads the review twice.
//
// A failed last-ordered lookup does not fail the review: the server flags it
// (`lastOrderedUnavailable`), every LAST ORDERED cell is left blank rather than showing
// the never-ordered glyph, and one DS info Banner at the top of the card says why.
//
// Sending needs a review on screen: the review is the Shopper's only check before the
// send (Story 8.6 has no confirmation step), so while no review has loaded for the cart
// (a failed first read included) the Submit card and the footer's Submit stay hidden and
// the error state's Try again is the way back, even when the basket store holds lines.
//
// Delivery and collection (Story 8.13, ADO-2022): the `FulfilmentCard` sits between the Submit
// card and the lines card (Figma 5006:3275), under the same condition as the Submit card (a cart
// with lines to send). It renders only when the cart offers the choice (a Value Centre cart, not
// LSN), so NDC and direct-delivery reviews are unchanged (AC4). What it shows is what every Submit
// button on the page sends (D-7).
//
// Failures: a failed re-read keeps the last good review for the cart on screen (so its
// rows, and the control the caller just used, stay mounted), shows an inline alert with
// Try again, and reads every total as unknown until a read succeeds; only a cart with
// no review loaded yet shows the full error state. A failed basket hydrate shows its own
// alert whose retry re-runs the hydrate; until then every line is read-only, named for
// its SKU only, since the page cannot tell whose it is.
//
// Free-delivery progress (user decision, ADO #2020, 2026-09-25): Story 8.8's
// `FreeDeliveryProgress` (review variant) with its `useFreeDeliveryStatus` read, reused
// as is, sits under the title and above the review card, as Figma places it (desktop
// 5006:2478 / 5006:3267, mobile 4393:12453 under "· Cart"). It renders nothing while
// loading, on error, or when the depot has no threshold.
//
// Cut-off (Story 8.5, ADO-2014, D-9): the `CutOffBanner` sits above the title (banner,
// title, free-delivery progress, depot card, as Figma 5006:2478 orders them), fed by
// `useDepotCutOff` for the session depot (the read is shared with the header). It renders
// nothing while loading, when the read fails (a failed refetch keeps the last answer up
// while it is retried, D-19), or when the depot runs no cut-off (AC4). The depot card's
// section header carries no pill (D-21, `figma/8-5/2026-10-05`).
//
// Auto-send (Story 8.15, ADO-2024, D-2, D-5): the `AutoSendToggle` sits beside the depot name in
// the depot card header (Figma 5008:3087 in 5006:2501), fed by the same `useDepotCutOff` read and
// hidden where the cart has no cut-off. Ticking it arms the cart with the reference and the
// fulfilment choice the page's Submit would send; while it is ticked, a change to either is
// re-saved. A failed auto-send of this cart (refused or missed, D-10) is an error banner above the
// Submit card (`AutoSendOutcomeBanners`), until it is dismissed.
//
// Sending (Story 8.6, ADO-2015, D-8/D-9): the Submit card sits under the free-delivery
// progress and above the depot card, as the frames place it (desktop 4976:1731, mobile
// 4393:12517), whenever the review holds lines. A failed send's message
// (`SubmitOrderFailure`) sits right under the card, and is shown whatever the lines, so it is
// still seen when a `cart-changed` refusal re-opens an empty cart; its "Try again" needs lines to
// send. Once the order is accepted the page becomes the order-sent page (Figma 5464:87367 /
// 5464:87949): the confirmation (`OrderSentConfirmation`) alone under the header, its "Order sent"
// heading the page's h1, with no Order Review title, cut-off banner, free-delivery progress, card
// or lines, until the Shopper switches depot or starts a new cart.
//
// The Order footer (Story 8.14, ADO-2023; Figma 4978:2213 / VC 5006:2662) sits under the lines
// card: the store's own order reference (whenever the review holds lines) beside Total cases,
// then Continue shopping and a second Submit order. Its button and the Submit card's go through
// the same `useSubmitOrder`, and its field writes the per-cart reference the send carries.
//
// Story 8.9 (ADO-2018)'s "Clear cart" (Figma 4978:2234) sits in the Order footer's actions
// beside "Continue shopping", as the frames draw it, and a confirmed clear hands focus to the
// page title.
//
// On mobile and tablet the DS Back to Top button (ADO-1779; Figma 5997:50532 / 5997:51424) floats
// over the review once the Shopper has scrolled two screens down, and hands focus to the page
// title. It is the content column's last child, so its in-flow reserve lets the footer's last
// action scroll clear of the button. The order-sent page does not carry it.
//
// The page follows the approved frames at every width
// (desktop 4976:1638, tablet 5329:13508, mobile 5324:12598). The title names the depot's cart
// ("Ambient · Cart") when the session knows the depot's name, else "Cart"; where the cart's
// entry names its account (Story 8.4, ADO-2013: every Value Centre account) the account's name
// follows the depot's, in the title, the depot card and the Submit card ("VC Dublin · Main
// grocery · Cart"), while the cut-off banner keeps the depot's name alone. The depot card
// carries the depot's accent (the same per-depot accent the depot choice screen draws) as its
// left strip, its header tint and each department band's tick; its header shows the depot
// name, "N items · N cases" and a collapse button that hides and shows the lines. Every group's
// column header lists the seven approved columns (4977:1745) without sort carets (column
// sorting is a follow-up); PRICE shows the unit price the server locked on the line, RSP the RRP
// locked with it (Pricing migration 17, ADO-2248, AC14; the placeholder when the line has none),
// MARGIN the retail margin locked with it (Story 11.10, ADO-1961, S-9; the placeholder when the
// line has none), and PACK/CASE shows the catalogue's pack facts. Where the store does not see its
// margin the review read sends none on any line (D-3), and the page drops the MARGIN column,
// header and cells, as the Foodservice Order Review frame draws it. Below
// 1024px the column header is hidden and each cell carries its own caption.
// Story 11.10's second PR (ADO-1961, MI-6, MI-7): from 1024px the FIRST group's MARGIN header
// carries the margin info button (frame M5), and no other header does; below 1024px there is no
// column header, so no button. The header's labels are hidden from assistive tech (each row's
// cells carry their own captions), each label on its own rather than the whole row, so the button
// stays reachable.
//
// Money: every figure is summed from the prices the server locked on
// the lines (`cartValue`), never from a product-surface price, over the rows on screen: an
// editable row counts the store's line (its live quantity at its locked unit price, the
// figure the stepper shows), a read-only row the review's line. Each department band
// shows its share of the order total ("N% of value", `valueShareLabel`), then its cases and
// value (Figma 4977:1739, tablet 5329:13541); the share is left out whenever the band's value
// or the order total is unknown. While the lines carry the provisional price the share is one
// of provisional values too. The order total, labelled "Total excl. VAT" (Story 8.11) because
// the line prices are net of VAT, sits under Total cases and Total deposit (ADO-2250, below) in
// the title (4976:1719) and the Order footer (4978:2237). Like the case totals, every value
// reads as unknown while the review is loading, after a failed re-read, and also while a line
// carries no locked price; an empty cart is worth zero.
//
// Deposit (ADO-2250, which supersedes Story 8.11's deferral of it): every deposit figure is
// summed from the deposits the server locked on the lines (`cartDeposit`), over exactly the rows
// each goods figure sums with `cartValue`, so an editable row counts the store's line (its live
// quantity at its locked rate) and a read-only row the review's line. "Total deposit" sits
// between Total cases and Total excl. VAT in the title and the Order footer, always drawn,
// €0.00 included (D-1); each department band shows "Deposit €X" after its value when the band's
// deposit is above zero or unknown (`showsDepositTotal`, D-4); each row shows its rate under its
// PRICE value (`CartLineRow`). The deposit is never part of Total excl. VAT, a band's value or
// its share. Like the goods figures, every deposit figure reads as unknown while the review is
// loading, after a failed re-read, and while a line's deposit is not known.
//
// Deliberately absent (spec Never list): delivery figures, a substitute link, the availability
// pills other than discontinued, the promotions block and the multi-buy toast.
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getBasketReview,
  type BasketReview,
  type BasketReviewGroup,
  type BasketReviewLine,
} from '../../api/basket-review.client';
import type { BasketLine } from '../../api/basket.client';
import { cartCallTargetAt, useCartTargetAt } from '../../basket/sessionCart';
import { cartTargetKey } from '../../api/cart-target';
import { valueShareLabel, type PricedLine } from '../../basket/cartMoney';
import {
  BackToTop,
  Banner,
  Collapse,
  CutOffBanner,
  FreeDeliveryProgress,
  Icon,
} from '../../design-system';
import { useSessionStore, depotNameFor } from '../../auth/useSessionStore';
import { useSessionDepotId } from '../../availability/session-depot';
import { CartLineRow, type RowControl } from '../../basket/CartLineRow';
import { useOnOrder } from '../../onOrder/useOnOrder';
import { useOnOrderStore } from '../../onOrder/useOnOrderStore';
import { useCoreMarks } from '../../basket/useCoreMarks';
import { useRbpLinkedLines } from '../../basket/useRbpLinkedLines';
import { rbpLinksHref } from '../../rbp/rbpLinksRoute';
import { ClearCartControl } from '../../basket/ClearCartControl';
import { focusNeighbourRow } from '../../basket/cartRowFocus';
import {
  RemovedCartLine,
  usePendingRowFocus,
  useRemovedLineAction,
} from '../../basket/RemovedCartLine';
import { OrderSentBanner } from '../../basket/OrderSentBanner';
import { AutoSendToggle } from '../../basket/AutoSendToggle';
import { AutoSendOutcomeBanners } from '../../basket/AutoSendOutcomeBanners';
import { ChangedWhileReviewingBanner } from './ChangedWhileReviewingBanner';
import {
  reviewPlacements,
  withRemovedReviewLines,
  type ReviewPlacement,
} from '../../basket/removedLines';
import {
  cartDeposit,
  cartSaving,
  cartSendsSavings,
  cartValue,
  casesLabel,
  promotionLines,
  showsDepositTotal,
  UNKNOWN_TOTAL,
} from '../../basket/cartTotals';
import { useIsWowEntry } from '../../basket/wowEntry';
import { formatMoney, type MoneyValue } from '../../shared/money';
import { packCaseLabel } from '../../basket/packCase';
import { useIsNarrowViewport } from '../../basket/useIsNarrowViewport';
import { TABLET_DOWN_MEDIA_QUERY } from '../../design-system/breakpoints';
import { MarginInfo } from '../../pricing/MarginInfo';
import { ORDER_REVIEW_MARGIN_INFO } from '../../pricing/marginInfoContent';
import { useBasketStore } from '../../basket/useBasketStore';
import { useFreeDeliveryStatus } from '../../basket/useFreeDeliveryStatus';
import { useDepotCutOff } from '../../basket/useDepotCutOff';
import { useOrderSubmissionStore } from '../../basket/useOrderSubmissionStore';
import { HhtSendGuardDialog } from '../HhtOrders/HhtSendGuardDialog';
import {
  accountNameOf,
  namesItsAccount,
  primaryEntryAtDepot,
} from '../../ordering/orderingChoices';
import { depotAccent } from '../Choose/depotAccent';
import { CartPageShell } from './CartPageShell';
import { OrderFooter } from './OrderFooter';
import { OrderSentConfirmation } from './OrderSentConfirmation';
import { FulfilmentCard } from './FulfilmentCard';
import { RbpMissedProductsCard } from './RbpMissedProductsCard';
import { rbpMissedProductsEnabled } from '../../rbp/rbpMissedProductsFlag';
import { SubmitOrderCard } from './SubmitOrderCard';
import { SubmitOrderFailure } from './SubmitOrderFailure';
import { useCartDeals } from '../../multibuy/useCartDeals';
import { flagDismissed, useDealSessionStore } from '../../multibuy/useDealSessionStore';
import { useDealDialogStore } from '../../multibuy/useDealDialogStore';
import { CartDealFlag, CartDealFlagLink, ReviewDealPill } from '../../multibuy/CartDealParts';
import { revealDealElement } from '../../multibuy/revealDealElement';
import type { MultibuyDeal } from '../../api/multibuy-deals.client';
import styles from './CartPage.module.css';

type ReviewState =
  | { readonly status: 'loading' }
  | {
      readonly status: 'ready';
      readonly review: BasketReview;
      /** The SKUs the store held when this review was read: the shared cart's lines as last
       *  read (Story 8.3, DV-2), the rows that are editable. */
      readonly ownSkus: ReadonlySet<string>;
      /** False when the store had never hydrated for this depot when the review was
       *  read (its hydrate failed): `ownSkus` is then empty for want of data, not
       *  because the cart holds no line. */
      readonly ownershipKnown: boolean;
      /** The latest re-read failed: this is the last good review, kept on screen so
       *  its rows (and the focus inside them) survive, with its totals unknown. */
      readonly refreshFailed: boolean;
    }
  | { readonly status: 'error' };

/** A read that succeeded, tagged with the depot it answered for. */
interface LoadedReview {
  /** The cart it was read for (`cartTargetKey`; Story 8.4, ADO-2013: the depot AND the
   *  account), or null with no depot. */
  readonly cartKey: string | null;
  readonly review: BasketReview;
  readonly ownSkus: ReadonlySet<string>;
  readonly ownershipKnown: boolean;
}

/** The latest settled read: the last good review for its depot, if any, plus whether
 *  the latest read after it failed. */
type SettledReview =
  | { readonly status: 'ready'; readonly loaded: LoadedReview; readonly refreshFailed: boolean }
  | { readonly status: 'error'; readonly cartKey: string | null };

const EMPTY_REVIEW: BasketReview = { groups: [], totalCases: 0, lastOrderedUnavailable: false };

/** A React key per line that survives a neighbour's removal: the SKU plus how many lines
 *  of the same SKU came before it in the group (lines from different sections can share
 *  a SKU). An index key would remount every later row, dropping the focus AC4 moved. */
function lineKeys(lines: readonly { readonly skuCode: string }[]): string[] {
  const seen = new Map<string, number>();
  return lines.map((line) => {
    const occurrence = seen.get(line.skuCode) ?? 0;
    seen.set(line.skuCode, occurrence + 1);
    return `${line.skuCode}#${occurrence}`;
  });
}

/**
 * The review for the session depot, read once the basket store has hydrated (or its
 * hydrate failed), and re-read on a depot switch, on a retry, once a failed hydrate
 * is retried successfully, and each time the store settles (its lines changed and no
 * edit is in flight). Each read records the SKUs the store held when it was made (the shared
 * cart as last read, Story 8.3), and whether the store had hydrated (so those SKUs
 * are known). An out-of-order response is dropped by a request sequence, so a slow
 * earlier read can never overwrite a newer one. Each settled read is tagged with its
 * cart (Story 8.4, ADO-2013: the depot AND the account), and a read for any cart but the
 * current one reads as loading, so a depot or account switch never shows the previous
 * cart's review under the new one, not even for one render; a change of cart also retires
 * any read in flight. A failed re-read keeps the last good review for the same cart
 * (marked `refreshFailed`); only a cart with no review loaded yet reads as an error.
 */
function useBasketReview(depotId: string | null): {
  readonly state: ReviewState;
  readonly retry: () => void;
} {
  const lines = useBasketStore((s) => s.lines);
  const settled = useBasketStore((s) => s.pending.size === 0);
  const hydrated = useBasketStore((s) => s.hydrated);
  // The hydrate has answered, one way or the other. A failed hydrate leaves no own
  // lines, so the review still loads, with every line read-only.
  const storeLoaded = useBasketStore((s) => s.hydrated || s.error);
  const [settledReview, setSettledReview] = useState<SettledReview | null>(null);
  const [attempt, setAttempt] = useState(0);
  const sequence = useRef(0);
  const cart = useCartTargetAt(depotId);
  const cartKey = cart === null ? null : cartTargetKey(cart);
  // The cart the last read was started for: a change of cart retires any read in flight,
  // even before the new cart's basket has loaded (the early returns below).
  const readFor = useRef<string | null>(cartKey);

  useEffect(() => {
    if (readFor.current !== cartKey) {
      readFor.current = cartKey;
      ++sequence.current;
    }
    if (!settled) return;
    if (cart === null) {
      ++sequence.current;
      setSettledReview({
        status: 'ready',
        loaded: { cartKey, review: EMPTY_REVIEW, ownSkus: new Set(), ownershipKnown: true },
        refreshFailed: false,
      });
      return;
    }
    if (!storeLoaded) return;
    const request = ++sequence.current;
    const ownSkus: ReadonlySet<string> = new Set(lines.map((line) => line.skuCode));
    getBasketReview(cart).then(
      (review) => {
        if (request === sequence.current) {
          setSettledReview({
            status: 'ready',
            loaded: { cartKey, review, ownSkus, ownershipKnown: hydrated },
            refreshFailed: false,
          });
        }
      },
      () => {
        if (request !== sequence.current) return;
        setSettledReview((previous) =>
          previous !== null && previous.status === 'ready' && previous.loaded.cartKey === cartKey
            ? { status: 'ready', loaded: previous.loaded, refreshFailed: true }
            : { status: 'error', cartKey },
        );
      },
    );
  }, [cart, cartKey, lines, settled, hydrated, storeLoaded, attempt]);

  const retry = useCallback(() => {
    // A kept review stays on screen (with its totals unknown) while the retry reads;
    // with none loaded yet the page shows loading again.
    setSettledReview((previous) =>
      previous !== null && previous.status === 'ready' ? previous : null,
    );
    setAttempt((value) => value + 1);
  }, []);
  let state: ReviewState;
  if (settledReview === null) {
    state = { status: 'loading' };
  } else if (settledReview.status === 'error') {
    state = settledReview.cartKey === cartKey ? { status: 'error' } : { status: 'loading' };
  } else {
    const { loaded, refreshFailed } = settledReview;
    state =
      loaded.cartKey === cartKey
        ? {
            status: 'ready',
            review: loaded.review,
            ownSkus: loaded.ownSkus,
            ownershipKnown: loaded.ownershipKnown,
            refreshFailed,
          }
        : { status: 'loading' };
  }
  return { state, retry };
}

/** The depot card's label while the depot's name is unresolved. */
const UNRESOLVED_DEPOT_LABEL = 'Your depot';

/** The depot's name when one resolved: not missing, not blank, and not the depot id that
 *  `depotNameFor` falls back to. */
function resolvedDepotName(
  depotNames: Readonly<Record<string, string>>,
  depotId: string | null,
): string | undefined {
  if (depotId === null) return undefined;
  const name = depotNameFor(depotNames, depotId).trim();
  return name === '' || name === depotId ? undefined : name;
}

/** "1 item" / "N items": the depot header's line count (Figma 4977:1732). */
function itemsLabel(count: number): string {
  return count === 1 ? '1 item' : `${count} items`;
}

/** The review columns after the thumbnail, in the order of the Figma table header
 *  (4977:1745). Each review row's cells carry the same captions. */
const REVIEW_COLUMNS = [
  'Name',
  'Last ordered',
  'RSP',
  'Pack/case',
  'Price',
  'Margin',
  'Quantity',
] as const;

/** A money figure, or UNKNOWN_TOTAL when it is not known (never a false €0.00). */
function moneyOrUnknown(value: MoneyValue | null): string {
  return value === null ? UNKNOWN_TOTAL : formatMoney(value);
}

function TitleTotal({
  totalCases,
  depositTotal,
  orderTotal,
}: {
  readonly totalCases: number | null;
  readonly depositTotal: MoneyValue | null;
  readonly orderTotal: MoneyValue | null;
}) {
  return (
    <div className={styles.titleTotals}>
      <div className={styles.titleTotal}>
        <span>Total cases</span>
        <span className={styles.totalValue} data-testid="cart-page-title-total-cases">
          {totalCases ?? UNKNOWN_TOTAL}
        </span>
      </div>
      <div className={styles.titleTotal}>
        <span>Total deposit</span>
        <span className={styles.totalValue} data-testid="cart-page-title-total-deposit">
          {moneyOrUnknown(depositTotal)}
        </span>
      </div>
      <div className={styles.titleTotal}>
        <span>Total excl. VAT</span>
        <span className={styles.orderTotalValue} data-testid="cart-page-title-order-total">
          {moneyOrUnknown(orderTotal)}
        </span>
      </div>
    </div>
  );
}

interface ReviewRow {
  readonly key: string;
  readonly line: BasketReviewLine;
  /** A line the store holds: the store's live quantity; any other row: the review's own
   *  per-line quantity. */
  readonly quantity: number;
  readonly readOnly: boolean;
  /** The line this row's money is counted from: the store's line for an editable row
   *  (its live quantity and locked price), the review's line otherwise. */
  readonly money: PricedLine;
}

/** The cases across `rows`, each at the quantity its row shows, plus (Pricing migration 31,
 *  ADO-1958) the free units its line earned, which count towards the cases, never the money. */
function rowCases(rows: readonly ReviewRow[]): number {
  return rows.reduce((sum, row) => sum + row.quantity + (row.line.freeUnitsEarned ?? 0), 0);
}

/**
 * The rows to render from the latest review. The first row of a SKU in `ownSkus` (the
 * store's SKUs when the review was read: since Story 8.3 the whole shared cart, whoever
 * added them) is the store's line: while the store still holds it, it is editable with
 * the store's live quantity; once the store has dropped it (an optimistic removal the
 * refetch has not confirmed yet) it is omitted, never left as a read-only row. Every
 * other row (a line added since the store's read) is read-only with the review's
 * quantity. A group whose rows were all omitted is dropped with them.
 */
function reviewRows(
  review: BasketReview | null,
  ownSkus: ReadonlySet<string> | null,
  storeLines: readonly BasketLine[],
): { readonly group: BasketReviewGroup; readonly lines: readonly ReviewRow[] }[] {
  if (review === null || ownSkus === null) return [];
  const liveLine = new Map(storeLines.map((line) => [line.skuCode, line]));
  const seenOwnSkus = new Set<string>();
  return review.groups.flatMap((group) => {
    const keys = lineKeys(group.lines);
    const lines = group.lines.flatMap((line, index): ReviewRow[] => {
      const key = keys[index] ?? line.skuCode;
      if (ownSkus.has(line.skuCode) && !seenOwnSkus.has(line.skuCode)) {
        seenOwnSkus.add(line.skuCode);
        const own = liveLine.get(line.skuCode);
        return own === undefined
          ? []
          : [{ key, line, quantity: own.quantity, readOnly: false, money: own }];
      }
      return [{ key, line, quantity: line.quantity, readOnly: true, money: line }];
    });
    return lines.length === 0 ? [] : [{ group, lines }];
  });
}

export function CartPage() {
  const depotId = useSessionDepotId();
  const depotNames = useSessionStore((s) => s.depotNames);
  const { state, retry } = useBasketReview(depotId);
  const freeDeliveryStatus = useFreeDeliveryStatus(depotId);
  const cartTarget = useCartTargetAt(depotId);
  // Story 8.4 (ADO-2013): the cut-off of the cart's own account.
  const cutOff = useDepotCutOff(cartTarget);
  // The depot's resolved name, or undefined. `depotNameFor` falls back to the depot id when
  // no name resolved, and a name equal to the id is that fallback, never a name, so the page
  // never prints the id: the title reads "Cart", the depot card "Your depot", and the Submit
  // card and cut-off banner leave the depot out.
  // The depot's accent, drawn from its ordering entry as the depot choice screen draws it:
  // the active entry, else the depot's primary entry (its DEPOT entry over a range sharing
  // the depot); none while the ordering choices are unknown (the card then draws a neutral
  // pair).
  const activeEntry = useSessionStore((s) => {
    const entries = s.orderingChoices?.entries;
    if (entries === undefined) return null;
    return (
      entries.find((entry) => entry.key === s.activeEntryKey) ??
      (depotId !== null ? primaryEntryAtDepot(entries, depotId) : null)
    );
  });
  // Enabler E8-G (ADO-3109), DA-8: the cart is its entry's, so it is named by the entry at the
  // depot ("Fruit & Veg" for Fruit & Veg's cart at depot 25, never Chill's name), else by the
  // depot's resolved name.
  const depotName =
    activeEntry !== null && activeEntry.depotId === depotId
      ? activeEntry.label
      : resolvedDepotName(depotNames, depotId);
  const accent = activeEntry !== null ? depotAccent(activeEntry) : undefined;
  // Story 8.4 (ADO-2013): the cart named as its order is ("VC Dublin · Main grocery"): the
  // depot's name, with the cart's account after it where the entry names its account.
  const entryNamesAccount = activeEntry !== null && namesItsAccount(activeEntry);
  const cartAccount = entryNamesAccount
    ? activeEntry.accounts.find(
        (account) => account.customerAccountCode === cartTarget?.customerAccountCode,
      )
    : undefined;
  const cartLabel =
    depotName !== undefined && cartAccount !== undefined
      ? `${depotName} · ${accountNameOf(cartAccount)}`
      : depotName;
  const pageTitle = cartLabel !== undefined ? `${cartLabel} · Cart` : 'Cart';
  const depotCardLabel = cartLabel ?? UNRESOLVED_DEPOT_LABEL;
  const [collapsed, setCollapsed] = useState(false);
  const storeLines = useBasketStore((s) => s.lines);
  const narrow = useIsNarrowViewport();
  // Below 1024px the column header is not drawn (CartPage.module.css), so the margin info button,
  // which only goes on it, is not rendered either (MI-6).
  const belowDesktop = useIsNarrowViewport(TABLET_DOWN_MEDIA_QUERY);
  const cardRef = useRef<HTMLElement>(null);
  const focusAfterRemove = useCallback(
    (skuCode: string, control: RowControl) =>
      focusNeighbourRow(cardRef.current, skuCode, control, () =>
        document.getElementById('cart-page-title')?.focus(),
      ),
    [],
  );
  const focusTitle = useCallback(() => document.getElementById('cart-page-title')?.focus(), []);

  const basketLoadFailed = useBasketStore((s) => s.error);
  const basketHydrated = useBasketStore((s) => s.hydrated);
  const review = state.status === 'ready' ? state.review : null;
  // Story 11.10 (ADO-1961), D-3: the review read sends `margin` on every line when the store sees
  // its margin and on none when it does not, so the column shows exactly when a line carries one.
  const marginShown = (review?.groups ?? []).some((group) =>
    group.lines.some((line) => line.margin !== undefined),
  );
  const rows = reviewRows(review, state.status === 'ready' ? state.ownSkus : null, storeLines);
  // Story 8.3 (ADO-2012), Task 11.5: a line removed while the Shopper was editing it stays in
  // its department, where it was, as a Removed Line (card 03). The review is re-read without
  // it, so the page remembers where each line sat while its conflict is shown.
  const lineConflicts = useBasketStore((s) => s.lineConflicts);
  // Story 8.3 (ADO-2012), card 10: under the Order sent banner the empty cart is the new one.
  const orderSent = useBasketStore((s) => s.orderSent !== null);
  const placementsRef = useRef<ReadonlyMap<string, ReviewPlacement>>(new Map());
  const placements = reviewPlacements(
    review?.groups ?? [],
    placementsRef.current,
    new Set(lineConflicts.keys()),
  );
  useEffect(() => {
    placementsRef.current = placements;
  });
  const groupedEntries = withRemovedReviewLines(rows, lineConflicts, placements);
  const removedLineAction = useRemovedLineAction(cardRef);
  // Story 8.3 (ADO-2012), card 10: "Add to a new cart" hands focus to the line once it is back.
  const requestRowFocus = usePendingRowFocus(cardRef);
  // Story 8.12 (ADO-2021), AC1: the review's products the Customer already has on order.
  useOnOrder(rows.flatMap((row) => row.lines.map((entry) => entry.line.skuCode)));
  const onOrderBySku = useOnOrderStore((s) => s.bySku);
  // The review lines' RBP core marks ("Core", "Core (link)").
  const coreMarks = useCoreMarks(
    rows.flatMap((row) => row.lines.map((entry) => entry.line.skuCode)),
  );
  // The discontinued lines with a child active in the cart's catalogue (RBP-D33).
  const rbpLinkedLines = useRbpLinkedLines(
    rows.flatMap((row) =>
      row.lines.filter((entry) => entry.line.discontinued).map((entry) => entry.line.skuCode),
    ),
  );
  const ownershipUnknown = state.status === 'ready' && !state.ownershipKnown;
  // A kept review after a failed re-read: its rows stay, but none of its totals is
  // current, so every total reads as unknown until a read succeeds.
  const totalsKnown = state.status === 'ready' && !state.refreshFailed;
  // Every total counts the rendered rows, so the cases, items and value beside each other
  // all follow the same lines the steppers show (the caller's own rows at their live
  // quantity), never the review's cases beside the live money.
  const allRows = rows.flatMap((group) => group.lines);
  const totalCases = totalsKnown && review !== null ? rowCases(allRows) : null;
  const orderTotal = totalsKnown ? cartValue(allRows.map((row) => row.money)) : null;
  // ADO-2250: the deposit over the same rows, never part of `orderTotal`.
  const orderDeposit = totalsKnown ? cartDeposit(allRows.map((row) => row.money)) : null;
  // ADO-2249 (Task 9.3, DEC-13 c): the savings over the same rows, on every entry except WOW, and
  // only once the server sends the lines' price types (DEC-6) and VITE_REAL_PRICES_ENABLED is on
  // (DEC-18).
  const wowEntry = useIsWowEntry();
  const savingLines = allRows.map((row) => row.money);
  const savings =
    !wowEntry && review !== null && cartSendsSavings(savingLines)
      ? {
          total: totalsKnown ? cartSaving(savingLines) : null,
          promotionLines: totalsKnown ? promotionLines(savingLines) : null,
        }
      : null;
  const totalItems = totalsKnown && review !== null ? allRows.length : null;
  // Pricing migration 32 (ADO-3985), T15, T16 and T19: the review lines' multi-buy deals. The
  // flag sits after the Submit card with "Show me ↓", each incomplete deal's nudge above its
  // department's lines with "See qualifying lines →" opening the deal's dialog over the review
  // (19, 20), and each line its pill (R2 17w, R1 17s). Submit is never blocked (amended AC5).
  const cartDeals = useCartDeals(
    allRows.map((row) => ({
      skuCode: row.line.skuCode,
      quantity: row.quantity,
      ...(row.line.freeUnitsEarned !== undefined
        ? { freeUnitsEarned: row.line.freeUnitsEarned }
        : {}),
      ...(row.line.multibuyOfferCode !== undefined
        ? { multibuyOfferCode: row.line.multibuyOfferCode }
        : {}),
    })),
    'review',
  );
  const dismissedNudges = useDealSessionStore((s) => s.dismissedNudges);
  const nudgedDeals = cartDeals.toFinish
    .map((row) => row.deal)
    .filter(
      (deal) =>
        !flagDismissed(dismissedNudges, deal.multibuyOfferCode, deal.progress.pooledQuantity),
    );
  /** The first department group holding one of the deal's lines takes its nudge (19). */
  const nudgesByGroup = new Map<string, MultibuyDeal[]>();
  for (const deal of nudgedDeals) {
    const holding = rows.find((group) =>
      group.lines.some((row) =>
        deal.products.some((product) => product.skuCode === row.line.skuCode),
      ),
    );
    if (holding === undefined) continue;
    const groupKey = holding.group.departmentName ?? '';
    nudgesByGroup.set(groupKey, [...(nudgesByGroup.get(groupKey) ?? []), deal]);
  }
  const openDealFor = (deal: MultibuyDeal) => {
    const sku = deal.products.find((product) =>
      allRows.some((row) => row.line.skuCode === product.skuCode),
    )?.skuCode;
    if (sku !== undefined)
      useDealDialogStore.getState().openDeal({ skuCode: sku, offerCode: deal.multibuyOfferCode });
  };
  const reviewFlag = cartDeals.flag;
  // "Show me ↓" goes to the first flagged deal's nudge, shown again if the shop had closed it
  // (review cycle 1: the first nudge on the page may be another deal's, or none).
  const showFlaggedNudge = (deal: MultibuyDeal | undefined) => {
    if (deal === undefined) return;
    useDealSessionStore.getState().restoreNudge(deal.multibuyOfferCode);
    requestAnimationFrame(() =>
      revealDealElement(
        document.querySelector<HTMLElement>(
          `[data-multibuy-nudge="${CSS.escape(deal.multibuyOfferCode)}"]`,
        ),
      ),
    );
  };
  const reviewPillOf = (sku: string) => {
    const pill = cartDeals.reviewPill(sku);
    return pill === null ? undefined : (
      <ReviewDealPill text={pill.text} note={pill.note} tone={pill.tone} />
    );
  };
  // Clear is offered while the cart has any line: the store's (the shared cart as last
  // read, Story 8.3), so the lines stay clearable while the review is loading or has
  // failed, or the review's, which may hold a line added since that read; a kept review
  // after a failed re-read still counts.
  const cartHasLines = storeLines.length > 0 || (review !== null && review.groups.length > 0);
  const retryHydrate = useCallback(() => {
    const cart = depotId === null ? null : cartCallTargetAt(depotId);
    if (cart !== null) void useBasketStore.getState().hydrate(cart);
  }, [depotId]);
  const confirmation = useOrderSubmissionStore((s) =>
    s.phase === 'sent' && s.depotId === depotId ? s.confirmation : null,
  );

  if (confirmation !== null) {
    return (
      <CartPageShell labelledBy="order-sent-heading">
        <OrderSentConfirmation
          confirmation={confirmation}
          accent={accent}
          namesAccount={entryNamesAccount}
        />
      </CartPageShell>
    );
  }

  return (
    <CartPageShell
      title={pageTitle}
      headerActions={
        <TitleTotal totalCases={totalCases} depositTotal={orderDeposit} orderTotal={orderTotal} />
      }
      banner={<CutOffBanner depotName={depotName} {...cutOff} />}
    >
      {/* Story 18.19 (ADO-4086): the main review is one column and the RBP missed-products widget
          its sibling, so CSS alone puts the widget beside it at 1200px and wider, and below the
          whole review (Order footer included) under that. One mount at every width. */}
      <div className={styles.reviewLayout}>
        <div className={styles.reviewMain}>
          <FreeDeliveryProgress status={freeDeliveryStatus} variant="review" />
          {/* Story 8.3 (ADO-2012), card 10: where card 09's banner sits, adaptive (decided with the
          user). */}
          <OrderSentBanner
            layout="adaptive"
            onAddToNewCart={(skuCode, added) =>
              requestRowFocus(skuCode, 'quantity', added, { untilLanded: true })
            }
          />
          {/* Story 8.15 (ADO-2024), D-10: this cart's auto-send that did not go. */}
          <AutoSendOutcomeBanners cart={cartTarget} />
          {/* Story 8.3 (ADO-2012), card 09: above the Submit card, as the frame draws it. */}
          {depotId !== null ? <ChangedWhileReviewingBanner depotId={depotId} /> : null}
          {depotId !== null && rows.length > 0 ? (
            <SubmitOrderCard depotId={depotId} cartName={cartLabel} />
          ) : null}
          {/* ADO-3985 (19): the incomplete multi-buy flag, after the Submit card. */}
          {reviewFlag === null || rows.length === 0 ? null : (
            <CartDealFlag
              title={reviewFlag.title}
              body={reviewFlag.reviewBody}
              action={
                <CartDealFlagLink
                  label="Show me"
                  icon="arrowDown"
                  onClick={() => showFlaggedNudge(reviewFlag.deals[0])}
                />
              }
              onDismiss={() =>
                useDealSessionStore.getState().dismissReviewFlags(
                  reviewFlag.deals.map((deal) => ({
                    offerCode: deal.multibuyOfferCode,
                    pooled: deal.progress.pooledQuantity,
                  })),
                )
              }
            />
          )}
          {depotId !== null && rows.length > 0 ? <FulfilmentCard depotId={depotId} /> : null}
          {depotId !== null ? (
            <SubmitOrderFailure depotId={depotId} canRetry={rows.length > 0} />
          ) : null}
          <section
            className={styles.card}
            aria-label="Cart lines"
            ref={cardRef}
            data-accent={accent}
          >
            {depotId !== null ? (
              <header className={styles.depotHeader}>
                <span className={styles.depotTitle}>
                  <Icon name="package" size={20} className={styles.depotIcon} />
                  <span className={styles.depotName}>{depotCardLabel}</span>
                  <AutoSendToggle variant="page" cutOffStatus={cutOff.status} />
                </span>
                <span className={styles.depotMeta} data-testid="cart-page-depot-total-cases">
                  {totalCases !== null && totalItems !== null
                    ? `${itemsLabel(totalItems)} · ${casesLabel(totalCases)}`
                    : UNKNOWN_TOTAL}
                </span>
                {rows.length > 0 ? (
                  <button
                    type="button"
                    className={styles.collapseButton}
                    aria-expanded={!collapsed}
                    aria-controls="cart-review-groups"
                    aria-label={`${collapsed ? 'Expand' : 'Collapse'} the ${depotCardLabel} lines`}
                    onClick={() => setCollapsed((value) => !value)}
                  >
                    <Icon name="caretDown" size={20} className={styles.collapseIcon} />
                  </button>
                ) : null}
              </header>
            ) : null}
            {basketLoadFailed && depotId !== null ? (
              <div className={styles.reviewError} role="alert">
                <p>We couldn&apos;t load your cart lines, so every line is shown read-only.</p>
                <button type="button" className={styles.retryButton} onClick={retryHydrate}>
                  Reload your lines
                </button>
              </div>
            ) : null}
            {review !== null && review.lastOrderedUnavailable && rows.length > 0 ? (
              <Banner type="info">
                We couldn&apos;t load when you last ordered these products.
              </Banner>
            ) : null}
            {state.status === 'error' ? (
              <div className={styles.reviewError} role="alert">
                <p>We couldn&apos;t load your order review.</p>
                <button type="button" className={styles.retryButton} onClick={retry}>
                  Try again
                </button>
              </div>
            ) : null}
            {state.status === 'ready' && state.refreshFailed ? (
              <div className={styles.reviewError} role="alert">
                <p>We couldn&apos;t update your order review, so its totals are unknown.</p>
                <button type="button" className={styles.retryButton} onClick={retry}>
                  Try again
                </button>
              </div>
            ) : null}
            {state.status === 'loading' ? (
              <p className={styles.loading} aria-live="polite">
                Loading your order review…
              </p>
            ) : null}
            {review !== null && review.groups.length === 0 && groupedEntries.length === 0 ? (
              <p className={styles.empty}>
                {orderSent ? 'Your new cart is empty.' : 'Your cart is empty.'}
              </p>
            ) : null}
            {/* ADO-2262: the lines collapse and expand through `Collapse`, the height only (they
            stay mounted, inert while collapsed). */}
            <Collapse id="cart-review-groups" open={!collapsed} fade={false}>
              {groupedEntries.map(({ group, entries }, groupIndex) => {
                const lines = entries.flatMap((entry) =>
                  entry.kind === 'line' ? [entry.line] : [],
                );
                // A blank heading (no department; the server folds a department named '' into
                // it, and a stray '' renders the same) still needs an accessible name for
                // heading and region navigation.
                const blankDepartment =
                  group.departmentName === null || group.departmentName === '';
                const groupValue = cartValue(lines.map((row) => row.money));
                const groupDeposit = cartDeposit(lines.map((row) => row.money));
                const share = totalsKnown ? valueShareLabel(groupValue, orderTotal) : null;
                return (
                  <section
                    key={
                      group.departmentName === null
                        ? 'no-department'
                        : `department:${group.departmentName}`
                    }
                    className={styles.group}
                    data-layout={narrow ? 'mobile' : 'desktop'}
                    data-no-margin={marginShown ? undefined : ''}
                    data-testid="cart-review-group"
                    aria-label={blankDepartment ? 'No department' : (group.departmentName ?? '')}
                  >
                    <div className={styles.groupBand} data-part="band">
                      <span className={styles.bandTick} data-part="band-tick" aria-hidden="true" />
                      <div className={styles.bandText} data-part="band-text">
                        <div className={styles.bandNameRow} data-part="band-name-row">
                          {/* The heading stays visibly blank for no department (SL1.0 parity, gate
                          decision 3) or a department named '', but keeps an accessible name
                          for heading navigation. */}
                          <h2
                            className={styles.groupName}
                            data-testid="cart-review-group-name"
                            aria-label={blankDepartment ? 'No department' : undefined}
                          >
                            {group.departmentName ?? ''}
                          </h2>
                          {share !== null ? (
                            <span
                              className={styles.groupShare}
                              data-testid="cart-review-group-share"
                            >
                              {share}
                            </span>
                          ) : null}
                        </div>
                        {narrow ? null : (
                          <span
                            className={styles.bandDivider}
                            data-part="band-divider"
                            aria-hidden="true"
                          />
                        )}
                        <span className={styles.groupCases} data-part="band-meta">
                          {totalsKnown ? (
                            <>
                              <span>{casesLabel(rowCases(lines))}</span>
                              {' · '}
                              <span data-testid="cart-review-group-value">
                                {moneyOrUnknown(groupValue)}
                              </span>
                              {showsDepositTotal(groupDeposit) ? (
                                <>
                                  {/* A space a screen reader hears between the value and the
                                  deposit; the visible gap is the margin. */}
                                  <span
                                    className={styles.visuallyHidden}
                                    data-part="band-deposit-separator"
                                  >
                                    {' '}
                                  </span>
                                  {/* Desktop and tablet set it apart with the band's divider; the
                                  mobile band's second line draws none. */}
                                  {narrow ? null : (
                                    <span
                                      className={`${styles.bandDivider} ${styles.metaDivider}`}
                                      data-part="band-deposit-divider"
                                      aria-hidden="true"
                                    />
                                  )}
                                  <span
                                    className={styles.groupDeposit}
                                    data-testid="cart-review-group-deposit"
                                  >
                                    Deposit {moneyOrUnknown(groupDeposit)}
                                  </span>
                                </>
                              ) : null}
                            </>
                          ) : (
                            UNKNOWN_TOTAL
                          )}
                        </span>
                      </div>
                    </div>
                    {/* ADO-3985 (19): an incomplete deal's nudge, above its department's lines. */}
                    {(nudgesByGroup.get(group.departmentName ?? '') ?? []).map((deal) => {
                      const nudge = cartDeals.reviewNudge(deal);
                      return (
                        <div
                          key={deal.multibuyOfferCode}
                          className={styles.dealNudge}
                          data-multibuy-nudge={deal.multibuyOfferCode}
                          tabIndex={-1}
                        >
                          <CartDealFlag
                            title={nudge.title}
                            body={nudge.body}
                            action={
                              <CartDealFlagLink
                                label="See qualifying lines"
                                icon="arrowRight"
                                onClick={() => openDealFor(deal)}
                              />
                            }
                            onDismiss={() =>
                              useDealSessionStore
                                .getState()
                                .dismissNudge(deal.multibuyOfferCode, deal.progress.pooledQuantity)
                            }
                          />
                        </div>
                      );
                    })}
                    {narrow ? null : (
                      <div className={styles.tableHead} data-testid="cart-review-table-head">
                        <span aria-hidden="true" />
                        {REVIEW_COLUMNS.filter((column) => marginShown || column !== 'Margin').map(
                          (column) =>
                            column === 'Margin' && groupIndex === 0 && !belowDesktop ? (
                              <span key={column} className={styles.headCell}>
                                <MarginInfo
                                  label={<span aria-hidden="true">{column}</span>}
                                  content={ORDER_REVIEW_MARGIN_INFO}
                                  size="default"
                                />
                              </span>
                            ) : (
                              <span key={column} className={styles.headCell} aria-hidden="true">
                                {column}
                              </span>
                            ),
                        )}
                      </div>
                    )}
                    <ul className={styles.list}>
                      {entries.map((entry) => {
                        if (entry.kind === 'removed') {
                          return (
                            <RemovedCartLine
                              key={`removed:${entry.skuCode}`}
                              skuCode={entry.skuCode}
                              name={entry.name}
                              message={entry.view.message}
                              action={entry.view.action}
                              announce={entry.announce}
                              variant={narrow ? 'pageMobile' : 'page'}
                              review
                              onAction={removedLineAction}
                            />
                          );
                        }
                        const { key, line, quantity, readOnly, money } = entry.line;
                        const onOrder = onOrderBySku[line.skuCode];
                        return (
                          <CartLineRow
                            key={key}
                            line={{
                              skuCode: line.skuCode,
                              quantity,
                              ...(money.unitPrice !== undefined
                                ? { unitPrice: money.unitPrice }
                                : {}),
                              // ADO-2250: the locked deposit per case, drawn under the PRICE value.
                              ...(money.depositPerUnit !== undefined
                                ? { depositPerUnit: money.depositPerUnit }
                                : {}),
                              // ADO-2249 (DEC-6): the price type and was price, for the row's pill,
                              // struck price and saving.
                              ...(money.priceKind !== undefined
                                ? { priceKind: money.priceKind }
                                : {}),
                              ...(money.wasPrice !== undefined ? { wasPrice: money.wasPrice } : {}),
                              // Story 8.10 (ADO-2019), Q7: the line's HHT part, for the "HHT" pill.
                              ...(line.hhtQuantity !== undefined
                                ? { hhtQuantity: line.hhtQuantity }
                                : {}),
                              // Story 18.18 (ADO-4037): the RBP Shopping List's part, for the
                              // "RBP NPR item" pill beside the core mark.
                              ...(line.rbpQuantity !== undefined
                                ? { rbpQuantity: line.rbpQuantity }
                                : {}),
                              // ADO-3985 (T16): the WOW free cases and the deal that earned them.
                              ...(line.freeUnitsEarned !== undefined
                                ? { freeUnitsEarned: line.freeUnitsEarned }
                                : {}),
                              ...(line.multibuyOfferCode !== undefined
                                ? { multibuyOfferCode: line.multibuyOfferCode }
                                : {}),
                            }}
                            deal={reviewPillOf(line.skuCode)}
                            readOnly={readOnly}
                            ownershipUnknown={ownershipUnknown}
                            name={line.name}
                            imageUrl={line.imageUrl}
                            variant={narrow ? 'pageMobile' : 'page'}
                            lastOrderedAt={line.lastOrderedAt}
                            lastOrderedUnknown={review?.lastOrderedUnavailable === true}
                            discontinued={line.discontinued}
                            packCase={packCaseLabel(line.packSize, line.unitOfMeasure)}
                            // ADO-2248 (AC14): the RRP the review line carries, locked at add.
                            rsp={line.rrp ?? null}
                            // ADO-1961 (S-9, D-3): the margin locked at add, or no column at all.
                            margin={line.margin ?? null}
                            marginHidden={!marginShown}
                            onFocusAfterRemove={focusAfterRemove}
                            conflict
                            {...(onOrder !== undefined && onOrder !== 'none' ? { onOrder } : {})}
                            {...(coreMarks[line.skuCode] !== undefined
                              ? { coreMark: coreMarks[line.skuCode] }
                              : {})}
                            {...(line.discontinued && rbpLinkedLines.has(line.skuCode)
                              ? { rbpLinksHref: rbpLinksHref(line.skuCode) }
                              : {})}
                          />
                        );
                      })}
                    </ul>
                  </section>
                );
              })}
            </Collapse>
          </section>
          <OrderFooter
            depotId={depotId}
            hasLines={rows.length > 0}
            totalCases={totalCases}
            orderTotal={orderTotal}
            depositTotal={orderDeposit}
            savings={savings}
            freeCases={wowEntry ? cartDeals.freeCases : null}
            clearCart={
              <ClearCartControl
                appearance="button"
                label="Clear cart"
                hasLines={cartHasLines}
                onCleared={focusTitle}
              />
            }
          />
        </div>
        {rbpMissedProductsEnabled() ? (
          <RbpMissedProductsCard
            cartKnown={basketHydrated && state.status === 'ready'}
            cartSkus={[
              ...storeLines.map((line) => line.skuCode),
              ...allRows.map((row) => row.line.skuCode),
            ]}
            cartDepartments={(review?.groups ?? []).flatMap((group) =>
              group.departmentName === null || group.departmentName.trim() === ''
                ? []
                : [group.departmentName],
            )}
            className={styles.reviewAside}
          />
        ) : null}
      </div>
      {/* Story 8.10 (ADO-2019), Q2: asked by either Submit button while HHT orders wait. */}
      <HhtSendGuardDialog />
      <BackToTop focusTargetId="cart-page-title" />
    </CartPageShell>
  );
}
